import React, { useState, useRef, useEffect } from "react";
import styles from "./code-input.module.css";

interface CodeInputProps {
  length?: number;
  onChange?: (code: string) => void;
  error: boolean;
}

const CodeInput: React.FC<CodeInputProps> = ({
  length = 6,
  onChange,
  error,
}) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(""));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (value: string, index: number) => {
    // Если вставлено несколько символов (например, из буфера), распределяем их по ячейкам
    if (value.length > 1) {
      const newCode = [...code];
      const values = value.split("").slice(0, length);

      values.forEach((char, i) => {
        if (index + i < length) {
          newCode[index + i] = char;
        }
      });

      setCode(newCode);

      // Перемещаем фокус на последнюю заполненную ячейку
      const lastFilledIndex = newCode.findIndex((item) => item === "");
      const focusIndex =
        lastFilledIndex === -1
          ? length - 1
          : Math.min(lastFilledIndex, length - 1);
      inputsRef.current[focusIndex]?.focus();

      return;
    }

    // Обычный ввод одного символа
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Автоматический переход к следующему полю
    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    // Обработка удаления (Backspace)
    if (e.key === "Backspace" && !code[index] && index > 0) {
      const newCode = [...code];
      newCode[index - 1] = "";
      setCode(newCode);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    if (pasteData) {
      const newCode = [...code];
      const values = pasteData.split("").slice(0, length);

      values.forEach((char, i) => {
        if (i < length) {
          newCode[i] = char;
        }
      });

      setCode(newCode);

      // Перемещаем фокус на последнюю заполненную ячейку
      const lastFilledIndex = newCode.findIndex((item) => item === "");
      const focusIndex =
        lastFilledIndex === -1
          ? length - 1
          : Math.min(lastFilledIndex, length - 1);
      inputsRef.current[focusIndex]?.focus();
    }
  };

  useEffect(() => {
    if (onChange) {
      onChange(code.join(""));
    }
  }, [code, onChange]);

  return (
    <div className={styles.codeContainer}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          maxLength={1}
          value={code[index]}
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className={`${styles.codeInput} ${error ? styles.error : ""}`}
          autoFocus={index === 0}
          inputMode="numeric"
        />
      ))}
    </div>
  );
};

export default CodeInput;
